import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import { formatDateAndTime } from "src/utils/dateAndTime";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  setIsTableLoading,
  setItems,
  setTotalItems,
  setFilterBy
} from "src/redux/slices/TabWorkHistoryMSMEApplicationSlice";
import { MSMEListResponse } from "src/models/MSME.model";
import {
  useGetMSMEApplicatioWorkHistoryQuery,
} from "src/services/MSMEService";
import TabHistoryApplicationMSMEListing from "./TabWorkHistoryApplicationMSMEListing";
import { applicationStatus } from "src/utils/history/applicationStatus";
import { BsCircleFill } from "react-icons/bs";
import { getColumns } from "src/utils/auth/getColumns";

import { Tooltip } from "@mui/material";
import { useLocation } from "react-router-dom";

// Params List
const paramList = [
  "_id",
  "propritorName",
  "email",
  "mobileNumber",
  "srn",
  "uniqueTransactionId",
  "status",
  "paymentCategory",
  "createdAt",
  "updatedAt"
];

const TabWorkHistoryApplicationMSMEListingWrapper = () => {
  //Status Mutation

  const { search } = useLocation()
  const queryParams = new URLSearchParams(search)

  const status: any | string | null = queryParams.get('status')

  const dispatch = useDispatch<AppDispatch>();
  const msmeState = useSelector(
    (state: RootState) => state.tabWorkHistoryMSMEApplication
  );
  const {
    items,
    rowsPerPage,
    searchValue,
    page,
    isTableLoading,
    filterBy,
    dateFilter,
  } = msmeState;
  // Fetching Applications
  const { data, isFetching, isLoading } = useGetMSMEApplicatioWorkHistoryQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: paramList,
    page: page,
    filterBy: filterBy,
    dateFilter: dateFilter,
    orderBy: "createdDateToShow",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting data
  useEffect(() => {
    if (!isFetching && !isLoading) {
      dispatch(setIsTableLoading(false));
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
    } else {
      dispatch(setIsTableLoading(true));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);

  React.useEffect(() => {
    let isExist = filterBy?.find((items: any) => items.fieldName === 'status')
    if (isExist) {
      if (status) {
        dispatch(setFilterBy([
          {
            fieldName: "status",
            value: [status],
          }
        ]))
      }
    }
    else {
      dispatch(setFilterBy((items: any) => [...items, { fieldName: "status", value: [] }]))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  const columns: columnTypes[] = [

    {
      field: "createdDateToShow",
      headerName: "Date - Time",
      noAuthRequired: true,
      flex: "flex-[1_1_0%]",
      renderCell: (row: MSMEListResponse) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatDateAndTime(row?.createdDateToShow, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatDateAndTime(row?.createdDateToShow, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "srn",
      headerName: "SRN",
      flex: "flex-[1_1_0%]",
      renderCell: (row: MSMEListResponse) => {
        return (
          <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
            {row.srn}
          </span>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: "flex-[1_1_0%]",
      renderCell: (row: MSMEListResponse) => {
        return (
          <span className="text-ellipsis overflow-hidden"> {row.email} </span>
        );
      },
    },
    {
      field: "mobileNumber",
      headerName: "Mobile",
      flex: "flex-[1_1_0%]",
      renderCell: (row: MSMEListResponse) => {
        return <span> {row.mobileNumber} </span>;
      },
    },
    {
      field: "adhaarNumber",
      headerName: "Aadhar Number",
      flex: "flex-[1_1_0%]",
      renderCell: (row: MSMEListResponse) => {
        return <span> {row.adhaarNumber} </span>;
      },
    },
    // {
    //   field: "acknowledgementNumber",
    //   headerName: "Acknowledgement Number",
    //   flex: "flex-[1_1_0%]",
    //   renderCell: (row: MSMEListResponse) => {
    //     return (
    //       <Tooltip title={row.acknowledgementNumber}>
    //         <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
    //           {row.acknowledgementNumber || "-"}
    //         </span>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      field: "applicationIndividualPrice",
      headerName: "Amount",
      flex: "flex-[1_1_0%]",
      renderCell: (row: MSMEListResponse) => {
        return (
          <Tooltip title={row?.applicationIndividualPrice}>
            <span className="text-green-500 text-[13px] text-ellipsis overflow-hidden font-semibold">
              &#8377; {row?.applicationIndividualPrice || "-"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "verifiedByName",
      headerName: "Verified By",
      flex: "flex-[1_1_0%]",
      renderCell: (row: MSMEListResponse) => {
        return (
          <Tooltip title={row?.verifiedByName}>
            <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden font-semibold">
              {row?.verifiedByName || "-"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "rejectedByName",
      headerName: "Rejected By",
      flex: "flex-[1_1_0%]",
      renderCell: (row: MSMEListResponse) => {
        return (
          <Tooltip title={row?.rejectedByName}>
            <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden font-semibold">
              {row?.rejectedByName || "-"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      noAuthRequired: true,
      field: "status",
      headerName: "Status",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: MSMEListResponse) => {
        return (
          <div className="flex flex-col gap-2 py-2">
            <span
              className={`font-medium rounded-full flex gap-2 items-center bg-slate-100 px-2 py-1 text-[13px]  ${applicationStatus[row?.status]?.className
                }`}
            >
              <BsCircleFill className="text-[10px]" />
              {applicationStatus[row?.status]?.label || "N/A"}{" "}
            </span>
            {/* {(row.status === "VERIFY") && (
              <ATMLoadingButton
                onClick={(e) => {
                  e.stopPropagation();
                  setMSMEIdVerfiy(row._id || "");
                  setIsOpenReuploadDialog(true);
                }}
                className="py-0 rounded-full text-sky-600  bg-white hover:bg-sky-600 hover:text-white transition-all"
              >
                <div className="text-[12px] py-1 ">
                  Reupload Acknowledgement
                </div>
              </ATMLoadingButton>
            )} */}
          </div>
        );
      },
    },
    {
      field: "rejectionReason",
      headerName: "Rejection Reason",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        return (
          <Tooltip title={row?.rejectionReason}>
            <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden font-semibold">
              {row?.rejectionReason || "-"}
            </span>
          </Tooltip>
        );
      },
    },
  ];
  // Handle Export

  return (
    <>
      <TabHistoryApplicationMSMEListing
        columns={getColumns(columns, "MSME_APPLICATIONS")}
        rows={items}
        isTableLoading={isTableLoading}
      />
      {/* {isOpenReuploadDialog && (
        <ReuploadAcknowledgementDialogWrapper
          serviceName="MSME"
          onClose={() => setIsOpenReuploadDialog(false)}
          onSubmit={(formData, onComplete) => {
            reuploadAcknowledgment({
              id: MSMEIdVerfiy || "",
              body: formData,
            }).then((res: any) => {
              onComplete();
              if (res?.error) {
                showToast("error", res?.error?.data?.message);
              } else {
                if (res?.data?.status) {
                  showToast("success", res?.data?.message);
                  setIsOpenReuploadDialog(false);
                } else {
                  showToast("error", res?.data?.message);
                }
              }
            });
          }}
        />
      )} */}

    </>
  );
};

export default TabWorkHistoryApplicationMSMEListingWrapper;
